import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  KLUC,
  VOLBY,
  aplikuj,
  nacitajVolbu,
  PREDVOLENA,
  sledujSystem,
  systemJeTmavy,
  ulozVolbu,
  vyhodnot,
} from "../features/vzhlad";

/**
 * Svetlý / tmavý režim pre celú aplikáciu — logika je vo features/vzhlad.js.
 *
 * Provider je úplne navonok (nad SiteGate), lebo farby platia aj pre
 * obrazovku s heslom a pre výber prevádzky.
 *
 *   const { volba, rezim, nastavVolbu } = useVzhlad();
 *   volba — čo si používateľ zvolil: "light" | "dark" | "auto"
 *   rezim — čo sa naozaj zobrazuje: "light" | "dark"
 */
const VzhladContext = createContext(null);

// Mimo providera (izolovaný test komponentu) — svetlý režim, prepínač nič nerobí.
const BEZ_PROVIDERA = { volba: PREDVOLENA, rezim: "light", nastavVolbu: () => {} };

export function VzhladProvider({ children }) {
  const [volba, setVolba] = useState(nacitajVolbu);
  const [systemTmavy, setSystemTmavy] = useState(systemJeTmavy);
  const rezim = vyhodnot(volba, systemTmavy);

  // Layout effect: atribút musí byť na <html> skôr, než prehliadač vykreslí
  // zmenu — inak by prepnutie na okamih ukázalo starý režim.
  useLayoutEffect(() => {
    aplikuj(rezim);
  }, [rezim]);

  useEffect(() => sledujSystem(setSystemTmavy), []);

  // Voľba zmenená v inej záložke. key === null znamená localStorage.clear().
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KLUC || e.key === null) setVolba(nacitajVolbu());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const nastavVolbu = useCallback((nova) => {
    if (!VOLBY.includes(nova)) return;
    ulozVolbu(nova);
    setVolba(nova);
  }, []);

  const hodnota = useMemo(() => ({ volba, rezim, nastavVolbu }), [volba, rezim, nastavVolbu]);

  return <VzhladContext.Provider value={hodnota}>{children}</VzhladContext.Provider>;
}

export function useVzhlad() {
  return useContext(VzhladContext) ?? BEZ_PROVIDERA;
}
